import { z } from 'zod'
import { isSimple } from '../acoustics/polygon'

export const CURRENT_VERSION = 4
export const MAX_SOURCES = 4
export const MAX_OBJECTS = 100

const vec3 = z.tuple([z.number(), z.number(), z.number()])
const material = z.enum(['concrete', 'metal', 'absorbent'])
const unit = z.number().min(0).max(1)
const point2 = z.tuple([z.number(), z.number()])
export const MAX_OUTLINE_POINTS = 64
export const MAX_HOLES = 8

export { FACES, type Face, ROOM_FACES, type RoomShape } from './faces'

export const EnclosureSchema = z.object({
  /** box: four straight walls. round: a cylindrical hall. ring: a round hall around a solid core column. polygon: any simple outline. */
  shape: z.enum(['box', 'round', 'ring', 'polygon']),
  /** Width (x), height (y), depth (z) in metres. Round and ring rooms use x = z = diameter. */
  size: vec3,
  /** Diameter of the central core column (ring rooms only; 0 otherwise). */
  core: z.number().min(0),
  /** Floor outline in metres, centred on the origin (polygon rooms only; empty otherwise). Width and depth in size follow its bounds. */
  outline: z.array(point2).max(MAX_OUTLINE_POINTS),
  materials: z.object({
    floor: material,
    ceiling: material,
    west: material,
    east: material,
    north: material,
    south: material,
    /** The curved outer wall of round and ring rooms. */
    wall: material,
    /** The core column of ring rooms. */
    core: material,
  }),
})

export const PrimitiveSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['box', 'cylinder', 'tube', 'prism']),
  position: vec3,
  rotation: vec3, // Euler YXZ, radians
  /** box: extents. cylinder and tube: outer diameter x, height (length) y, outer diameter z. */
  size: vec3,
  material,
  /** Tube wall thickness in metres (ignored by other kinds). */
  wall: z.number().min(0.01).max(500),
  /** Which ends of a tube are closed with a plate of wall thickness. */
  caps: z.enum(['none', 'top', 'bottom', 'both']),
  /** Prism footprint in unit coordinates (scaled by size x and z); holes are extra contours. Empty for other kinds. */
  outline: z.array(point2).max(MAX_OUTLINE_POINTS),
  holes: z.array(z.array(point2).max(MAX_OUTLINE_POINTS)).max(MAX_HOLES),
})
export type Caps = 'none' | 'top' | 'bottom' | 'both'

export const FAMILIES = ['vent', 'motor', 'drone', 'drip', 'turbine', 'fan', 'press', 'grind', 'creak'] as const
export type Family = (typeof FAMILIES)[number]

export const ClipSettingsSchema = z.object({
  assetId: z.string(),
  fileName: z.string(),
  loop: z.boolean(),
  /** Semitones. */
  pitch: z.number().min(-24).max(24),
  /** Highpass / lowpass corners in Hz. */
  highpass: z.number().min(20).max(2000),
  lowpass: z.number().min(200).max(20000),
  retrigger: z.object({
    enabled: z.boolean(),
    interval: z.number().min(0.2).max(120), // mean seconds between starts
    irregularity: unit,
  }),
})

export const SourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: vec3,
  kind: z.enum(['generator', 'clip']),
  family: z.enum(FAMILIES),
  /** Three family-specific controls, all normalised 0..1 (see FAMILY_INFO). */
  params: z.tuple([unit, unit, unit]),
  seed: z.number().int().min(0).max(0xffffffff),
  clip: ClipSettingsSchema.nullable(),
  /** Linear level at the source's reference radius; up to +40 dB for machines that are heard across a megastructure. */
  gain: z.number().min(0).max(100),
  /** Reference radius in metres: level is constant inside it and falls as 1/r beyond. A 40 m turbine is not a point. */
  size: z.number().min(0.1).max(500),
  mute: z.boolean(),
  solo: z.boolean(),
  playing: z.boolean(),
  /** Per-source high-frequency cut in Hz. */
  hfCut: z.number().min(500).max(20000),
})

export const ListenerSchema = z.object({
  position: vec3,
  yaw: z.number(),
  pitch: z.number(),
})

export const SettingsSchema = z.object({
  snapEnabled: z.boolean(),
  snapStep: z.number().min(0.05).max(10),
  masterGain: z.number().min(0).max(1.5),
})

export const SceneSchema = z
  .object({
    name: z.string(),
    enclosure: EnclosureSchema,
    objects: z.array(PrimitiveSchema).max(MAX_OBJECTS),
    sources: z.array(SourceSchema).max(MAX_SOURCES),
    listener: ListenerSchema,
    settings: SettingsSchema,
  })
  .superRefine((scene, ctx) => {
    if (scene.enclosure.shape === 'polygon' && !isSimple(scene.enclosure.outline)) {
      ctx.addIssue({
        code: 'custom',
        path: ['enclosure', 'outline'],
        message: 'The room outline must be a simple polygon (at least 3 corners, no crossing edges).',
      })
    }
    scene.objects.forEach((o, i) => {
      if (o.kind !== 'prism') return
      if (!isSimple(o.outline) || !o.holes.every(isSimple)) {
        ctx.addIssue({ code: 'custom', path: ['objects', i, 'outline'], message: `"${o.name}" has an invalid outline.` })
      }
    })
  })
export type Vec3 = [number, number, number]
export type Enclosure = z.infer<typeof EnclosureSchema>
export type Primitive = z.infer<typeof PrimitiveSchema>
export type ClipSettings = z.infer<typeof ClipSettingsSchema>
export type Source = z.infer<typeof SourceSchema>
export type Listener = z.infer<typeof ListenerSchema>
export type Settings = z.infer<typeof SettingsSchema>
export type SceneDoc = z.infer<typeof SceneSchema>

/** Envelope written to disk / ZIP. */
export const ProjectFileSchema = z.object({
  format: z.literal('megaspace-project'),
  version: z.number().int().min(1),
  scene: z.unknown(),
  assets: z.array(z.object({ id: z.string(), fileName: z.string(), path: z.string(), mime: z.string() })).default([]),
})
export type ProjectFile = z.infer<typeof ProjectFileSchema>

export interface FamilyInfo {
  label: string
  controls: [string, string, string]
  defaults: [number, number, number]
}

export const FAMILY_INFO: Record<Family, FamilyInfo> = {
  vent: { label: 'Vent', controls: ['Airflow', 'Tone', 'Fluctuation'], defaults: [0.55, 0.4, 0.5] },
  motor: { label: 'Motor', controls: ['Speed', 'Roughness', 'Instability'], defaults: [0.4, 0.35, 0.25] },
  drone: { label: 'Drone', controls: ['Pitch', 'Richness', 'Drift'], defaults: [0.3, 0.45, 0.4] },
  drip: { label: 'Drip', controls: ['Rate', 'Size', 'Irregularity'], defaults: [0.3, 0.5, 0.7] },
  turbine: { label: 'Turbine', controls: ['Speed', 'Load', 'Wobble'], defaults: [0.35, 0.4, 0.3] },
  fan: { label: 'Fan', controls: ['Speed', 'Blades', 'Turbulence'], defaults: [0.4, 0.35, 0.3] },
  press: { label: 'Press', controls: ['Rate', 'Weight', 'Irregularity'], defaults: [0.35, 0.6, 0.35] },
  grind: { label: 'Grinder', controls: ['Pressure', 'Pitch', 'Chatter'], defaults: [0.5, 0.45, 0.5] },
  creak: { label: 'Creak', controls: ['Stress', 'Pitch', 'Strain'], defaults: [0.45, 0.35, 0.5] },
}

export const ENCLOSURE_LIMITS = { min: [4, 2, 4], max: [1500, 600, 1500] } as const

let counter = 0
export function newId(prefix: string): string {
  counter++
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`
}
